/* Case Study V2 interactions. Progressive enhancement only: without this module
   the page still reads, the chapter anchors still work and figures render at
   full height.

   A. Figures open full-window, centred on the viewport. One at a time,
      Esc or the backdrop closes.
   B. The chapter navigator condenses and docks once the in-flow pill row
      scrolls past DOCK_LINE.

   Styles live in scss/widgets/_case-study-v2.scss.

   Runs on every page, so the first thing it does is look for a .cs2 root and
   leave if there is not one. */

const ERROR_MESSAGES = {
	INIT_FAILED: 'Case Study V2: Failed to initialize',
};

// initializeComponents() runs once per page load, so in practice this only ever
// runs once. It is written to survive a second call anyway, because this module
// is the one that would leak if it did not: the Esc handler is on document, the
// scroll and resize handlers are on window, and the lightbox is appended to
// <body>. None of those go away with the markup they belong to.
let teardown = null;

/**
 * Initialize Case Study V2 interactions
 * @returns {void}
 */
export const initializeCaseStudyV2 = () => {
	try {
		if (teardown) {
			teardown();
			teardown = null;
		}

		const root = document.querySelector('.cs2');
		if (!root) {
			return;
		}

		// Filled in as listeners are bound, run in reverse on the next init.
		const cleanups = [];
		teardown = () => {
			while (cleanups.length) {
				cleanups.pop()();
			}
		};

		// The site header is fixed, so anything in the top --cs2-header pixels is
		// behind it. A flat 72 sat above that line: following the first chapter's
		// anchor parks the pill row at scroll-margin-top (header + 24), which is
		// under the header but still below 72, so the row was hidden and the dock
		// had not taken over - no navigation visible at all. Handing over a little
		// before the row slips under the header closes that gap.
		const headerHeight = parseFloat(
			getComputedStyle(document.documentElement).getPropertyValue('--cs2-header')
		) || 78;
		const DOCK_LINE = headerHeight + 40;
		const ACTIVE_LINE = 220; // active chapter = last one whose top is at or above this

		// Tells the stylesheet the enhancement is live: only then does a figure get
		// capped, and only then does the docked navigator become displayable.
		root.classList.add('cs2-js');

		/* ---------------------------------------------------------------- A. figures */

		/* Opening a figure moves it into a full-window overlay rather than growing it
		   in the column, so a dense diagram gets the whole screen and lands centred
		   on the window. The overlay is built once and appended to <body>. */

		let openFigure = null;
		let lastFocus = null;
		let lightbox = null;

		const buildLightbox = () => {
			const el = document.createElement('div');
			el.className = 'cs2-lightbox';
			el.setAttribute('role', 'dialog');
			el.setAttribute('aria-modal', 'true');
			el.hidden = true;

			const close = document.createElement('button');
			close.type = 'button';
			close.className = 'cs2-lightbox__close';

			const inner = document.createElement('figure');
			inner.className = 'cs2-lightbox__inner';

			const img = document.createElement('img');
			const caption = document.createElement('figcaption');
			caption.className = 'cs2-lightbox__caption';

			inner.append(img, caption);
			el.append(close, inner);
			document.body.appendChild(el);

			close.addEventListener('click', closeFigure);

			// Clicking the backdrop closes; clicking the picture itself does not.
			el.addEventListener('click', (event) => {
				if (!inner.contains(event.target)) {
					closeFigure();
				}
			});

			// One focusable child, so holding it is a complete trap.
			el.addEventListener('keydown', (event) => {
				if (event.key === 'Tab') {
					event.preventDefault();
					close.focus();
				}
			});

			cleanups.push(() => {
				// Closing first: it is what lifts the scroll lock, and it needs the
				// element still to be here.
				closeFigure();
				el.remove();
				lightbox = null;
			});

			lightbox = { el, close, img, caption };
			return lightbox;
		};

		const lockScroll = (lock) => {
			// Pad for the scrollbar the lock removes, so the page does not jump sideways.
			const gap = window.innerWidth - document.documentElement.clientWidth;
			document.body.style.overflow = lock ? 'hidden' : '';
			document.body.style.paddingRight = lock && gap > 0 ? `${gap}px` : '';
			document.body.classList.toggle('cs2-lightbox-open', lock);
		};

		const setButton = (figure, expanded) => {
			const button = figure.querySelector('[data-cs2-figure-toggle]');
			if (button) {
				button.setAttribute('aria-expanded', String(expanded));
				button.textContent = expanded ? button.dataset.collapseLabel : button.dataset.expandLabel;
			}
		};

		function closeFigure() {
			if (!openFigure) {
				return;
			}
			setButton(openFigure, false);
			openFigure = null;

			lightbox.el.classList.remove('is-open');
			lightbox.el.hidden = true;
			lockScroll(false);

			if (lastFocus) {
				lastFocus.focus();
				lastFocus = null;
			}
		}

		const openLightbox = (figure) => {
			const source = figure.querySelector('[data-cs2-figure-frame] img');
			const button = figure.querySelector('[data-cs2-figure-toggle]');
			if (!source) {
				return;
			}

			const box = lightbox || buildLightbox();

			box.img.src = source.currentSrc || source.src;
			if (source.srcset) {
				box.img.srcset = source.srcset;
				// Ask for a candidate sized for the window, not for the text column.
				box.img.sizes = '96vw';
			} else {
				// Cleared, not left alone: an SVG has no srcset, and a leftover one
				// from the previously opened figure would win over src.
				box.img.removeAttribute('srcset');
				box.img.removeAttribute('sizes');
			}
			box.img.alt = source.alt || '';

			const caption = figure.querySelector('.cs2-figure__caption-text');
			box.caption.textContent = caption ? caption.textContent : '';
			box.el.setAttribute('aria-label', source.alt || box.caption.textContent);
			box.close.textContent = button ? button.dataset.collapseLabel : '';

			lastFocus = document.activeElement;
			openFigure = figure;
			setButton(figure, true);

			box.el.hidden = false;
			lockScroll(true);
			requestAnimationFrame(() => box.el.classList.add('is-open'));
			box.close.focus();
		};

		const toggleFigure = (figure) => {
			if (figure === openFigure) {
				closeFigure();
			} else {
				closeFigure();
				openLightbox(figure);
			}
		};

		root.querySelectorAll('[data-cs2-figure="expandable"]').forEach((figure) => {
			const button = figure.querySelector('[data-cs2-figure-toggle]');
			const frame = figure.querySelector('[data-cs2-figure-frame]');

			if (button) {
				button.hidden = false;
				button.addEventListener('click', () => toggleFigure(figure));
			}
			// The frame is a convenience target; the button is the real control, so
			// the frame stays out of the tab order and out of the a11y tree.
			if (frame) {
				frame.addEventListener('click', () => toggleFigure(figure));
			}
		});

		const onKeydown = (event) => {
			if (event.key === 'Escape') {
				closeFigure();
			}
		};
		document.addEventListener('keydown', onKeydown);
		cleanups.push(() => document.removeEventListener('keydown', onKeydown));

		/* ------------------------------------------------------------ B. navigator */

		const inflow = root.querySelector('[data-cs2-nav]');
		const dock = root.querySelector('[data-cs2-dock]');
		const chapters = Array.from(root.querySelectorAll('[data-cs2-chapter]'));

		if (inflow && dock && chapters.length) {
			const links = Array.from(root.querySelectorAll('[data-cs2-nav-link], [data-cs2-dock-link]'));
			const idOf = (link) => link.dataset.cs2NavLink || link.dataset.cs2DockLink;
			let activeId = null;
			let docked = false;

			const setActive = (id) => {
				if (id === activeId) {
					return;
				}
				activeId = id;
				links.forEach((link) => {
					const isActive = idOf(link) === id;
					link.classList.toggle('is-active', isActive);
					if (isActive) {
						link.setAttribute('aria-current', 'true');
					} else {
						link.removeAttribute('aria-current');
					}
				});
			};

			// Exactly the rule in the spec: the last chapter whose top edge has
			// reached ACTIVE_LINE, defaulting to the first before any has.
			const chapterAtScrollPosition = () => {
				let current = chapters[0];
				for (const chapter of chapters) {
					if (chapter.getBoundingClientRect().top > ACTIVE_LINE) {
						break;
					}
					current = chapter;
				}
				return current.dataset.cs2Chapter;
			};

			// Following an anchor smooth-scrolls past every chapter in between, and
			// reading the scroll position on the way makes each one light up and widen
			// in turn. A click therefore pins its own target, and the pin lifts the
			// moment the scroll actually arrives there.
			let pinnedId = null;
			let pinExpiry = 0;
			let pinTimer = null;

			const clearPin = () => {
				pinnedId = null;
				clearTimeout(pinTimer);
			};

			const computeActive = () => {
				if (pinnedId) {
					// Arrived, or took too long to. The deadline is what stops a manual
					// scroll straight after a click from holding the pin open forever.
					if (chapterAtScrollPosition() === pinnedId || Date.now() > pinExpiry) {
						clearPin();
					} else {
						return;
					}
				}
				setActive(chapterAtScrollPosition());
			};

			links.forEach((link) => {
				link.addEventListener('click', () => {
					const id = idOf(link);
					if (!id) {
						return;
					}
					// No preventDefault: the browser still does the scrolling.
					pinnedId = id;
					pinExpiry = Date.now() + 1500;
					setActive(id);

					// Backstop for the case where the click scrolls nowhere, so no
					// further frames arrive to lift the pin.
					clearTimeout(pinTimer);
					pinTimer = setTimeout(() => {
						clearPin();
						setActive(chapterAtScrollPosition());
					}, 1550);
				});
			});

			const setDocked = (next) => {
				if (next === docked) {
					return;
				}
				// Hiding an element that holds focus drops the caret to the top of the
				// tab order, which is what happens when someone tabs to a dock pill and
				// then scrolls back up with the keyboard. Hand focus over first.
				if (!next && dock.contains(document.activeElement)) {
					const id = idOf(document.activeElement);
					const twin = (id && inflow.querySelector(`[data-cs2-nav-link="${CSS.escape(id)}"]`))
						|| inflow.querySelector('[data-cs2-nav-link]');
					if (twin) {
						twin.focus({ preventScroll: true });
					}
				}
				docked = next;
				dock.hidden = !next;
				dock.classList.toggle('is-docked', next);
			};

			// The rail is fixed and vertically centred, so left alone it rides over the
			// closing CTA and on into the footer. Past a point it stops being centred
			// on the window and parks against the end of the chapter content instead,
			// so it scrolls away with the page like everything else.
			const RAIL_GAP = 24;
			const stopAt = root.querySelector('.cs2-closing');
			const isVerticalRail = () => window.matchMedia('(min-width: 1180px)').matches;

			const clampDock = () => {
				if (dock.hidden || !isVerticalRail()) {
					dock.style.top = '';
					return;
				}

				// The rail is translated up by half its own height, so `top` is where
				// its middle sits, not its top edge.
				const half = dock.offsetHeight / 2;
				const limit = (stopAt
					? stopAt.getBoundingClientRect().top
					: root.getBoundingClientRect().bottom) - RAIL_GAP;

				if (window.innerHeight / 2 + half > limit) {
					dock.style.top = `${Math.round(limit - half)}px`;
				} else {
					dock.style.top = '';
				}
			};

			const measure = () => {
				setDocked(inflow.getBoundingClientRect().bottom < DOCK_LINE);
				computeActive();
				clampDock();
			};

			if ('IntersectionObserver' in window) {
				new IntersectionObserver(([entry]) => {
					setDocked(!entry.isIntersecting && entry.boundingClientRect.top < DOCK_LINE);
					clampDock();
				}, { rootMargin: `-${DOCK_LINE}px 0px 0px 0px`, threshold: 0 }).observe(inflow);

				const active = new IntersectionObserver(computeActive, {
					rootMargin: `-${ACTIVE_LINE}px 0px -60% 0px`,
					threshold: 0
				});
				chapters.forEach((chapter) => active.observe(chapter));
			}

			// The observer band above is `0.4 * viewportHeight - 220` tall: it
			// collapses to nothing below about 550px of viewport height (a phone held
			// sideways) and is only a few dozen pixels tall a little above that, where
			// a fast fling can skip straight over it. So this is not an
			// IntersectionObserver fallback, it runs alongside as the correctness net.
			// One rAF-throttled read per frame of scrolling.
			let ticking = false;
			const onScroll = () => {
				// Two rect reads and at most one style write, so it is cheap enough to
				// run unthrottled. It also has to: the rail's resting place changes
				// with every pixel once the closing CTA is near, and a frame skipped
				// here is a frame where the rail sits on top of the CTA.
				clampDock();

				if (ticking) {
					return;
				}
				ticking = true;
				requestAnimationFrame(() => {
					ticking = false;
					measure();
				});
			};

			window.addEventListener('scroll', onScroll, { passive: true });
			window.addEventListener('resize', onScroll, { passive: true });
			cleanups.push(() => {
				window.removeEventListener('scroll', onScroll);
				window.removeEventListener('resize', onScroll);
			});

			measure();
		}
	} catch (error) {
		console.error(ERROR_MESSAGES.INIT_FAILED, error);
	}
};
